import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { fmtDateTime } from '@rydres/ui';
import { driverApi } from '../lib/api.js';
import { useLiveRefresh } from '../lib/live.js';
import type { DriverNotification } from '../lib/types.js';

/** The bell on Today, with how many updates are new. */
export function Bell() {
  const [n, setN] = useState(0);
  const load = useCallback(() => {
    driverApi.unreadNotifications().then((r) => setN(r.unread), () => undefined);
  }, []);
  useLiveRefresh(load);
  return (
    <Link to="/updates" aria-label={n ? `Updates, ${n} new` : 'Updates'} className="relative flex h-10 w-10 items-center justify-center rounded-full bg-card2 text-ink">
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M6 8a6 6 0 1112 0c0 7 3 9 3 9H3s3-2 3-9M10.3 21a2 2 0 003.4 0" />
      </svg>
      {n > 0 && <span className="absolute -right-0.5 -top-0.5 min-w-4 rounded-full bg-danger px-1 text-center text-[10px] font-black leading-4 text-card">{n > 99 ? '99+' : n}</span>}
    </Link>
  );
}

/** Every update about the driver's rides; opening the list marks them seen. */
export function Updates() {
  const [list, setList] = useState<DriverNotification[] | null>(null);
  useEffect(() => {
    driverApi.notifications().then(
      (r) => {
        setList(r.notifications);
        if (r.unread) void driverApi.readNotifications().catch(() => undefined);
      },
      () => setList([]),
    );
  }, []);
  return (
    <div className="space-y-4">
      <h1 className="text-2xl font-black">Updates</h1>
      {list === null ? (
        <p className="text-sm text-muted">Loading…</p>
      ) : list.length === 0 ? (
        <p className="text-sm text-muted">Nothing yet. New requests and changes to your rides show up here.</p>
      ) : (
        <ul className="space-y-2">
          {list.map((n) => {
            const body = (
              <>
                <div className="flex items-baseline justify-between gap-3">
                  <span className={n.read ? 'font-bold' : 'font-black'}>{n.title}</span>
                  <span className="shrink-0 text-xs text-muted">{fmtDateTime(n.at)}</span>
                </div>
                <p className="mt-0.5 text-sm text-ink2">{n.body}</p>
              </>
            );
            return (
              <li key={n.id}>
                {n.url ? <Link to={n.url} className={`block rounded-2xl border bg-card p-3 ${n.read ? 'border-line' : 'border-accent'}`}>{body}</Link> : <div className="rounded-2xl border border-line bg-card p-3">{body}</div>}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
